import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Container, Typography, Paper, Box, Button, Drawer, IconButton } from '@mui/material';
import { Chat as ChatIcon } from '@mui/icons-material';
import NavigationPanel from './NavigationPanel';
import DynamicForm from './DynamicForm';
import PreviewPanel from './PreviewPanel';
import { useApiClient } from '../../config/axios';
import ChatbotPanel from '../Chatbot/ChatbotPanel';

// TypeScript interfaces
interface NavigationRow {
  topNav: string;
  leftNav: string;
  viewLabel: string;
  configName: string;
  viewKey: string;
  topDown: boolean;
  bottomUp: boolean;
}

interface ViewItem {
  item_id: string;
  parent_item_id?: string | null;
  data_index?: string;
  label_text?: string;
  renderer?: string;
  width?: number | null;
  pinned?: number | null;
  seq_no?: number;
  role?: string;
  extra_props?: Record<string, any>;
  _isNew?: boolean;
  [key: string]: any;
}

/* ───────── helpers for PreviewPanel ────────── */

/** merge extra_props into the row itself and
 *  drop DB bookkeeping columns we don't want
 */
function normaliseRow(r: ViewItem) {
  const {
    item_id, parent_item_id,          // keep for tree building
    data_index, label_text, renderer, width, pinned,
    extra_props = {},
    ...dbJunk                         // ← created_at, updated_at, locked, …
  } = r;

  // Parse extra_props if it's a JSON string (from database)
  let parsedExtraProps = {};
  if (extra_props) {
    if (typeof extra_props === 'string') {
      try {
        parsedExtraProps = JSON.parse(extra_props);
      } catch (e) {
        console.warn('Failed to parse extra_props JSON:', extra_props);
        parsedExtraProps = {};
      }
    } else if (typeof extra_props === 'object') {
      parsedExtraProps = extra_props;
    }
  }

  return {
    ...(parsedExtraProps || {}),      // <- spread parsed extra_props first
    ...(label_text ? { text: label_text } : {}),
    ...(data_index ? { dataIndex: data_index } : {}),
    ...(renderer ? { renderer } : {}),
    ...(width ? { width } : {}),
    ...(pinned ? { pinned } : {}),
    item_id,
    parent_item_id
  };
}

/** build the nested `.view` array exactly like
 *  the original *.viewdefn* files
 */
function buildView(items: ViewItem[]) {
  const byId = new Map();          // item_id -> node
  const roots = [];                 // top‑level nodes

  // 1/ create a plain JS node for each row
  items.forEach(r => {
    const node = normaliseRow(r);
    (node as any).view = [];                  // space for children
    byId.set(node.item_id, node);
  });

  // 2/ link children to their parents
  items.forEach(r => {
    const node = byId.get(r.item_id);
    if (r.parent_item_id && byId.has(r.parent_item_id)) {
      byId.get(r.parent_item_id).view.push(node);
    } else {
      roots.push(node);
    }
  });

  // 3/ prune empty "view" arrays
  const prune = (n: any) => {
    // if there's no view array (or it's not an array), skip
    if (!Array.isArray(n.view)) return;
    if (n.view.length === 0) {
      delete n.view;
    } else {
      n.view.forEach(prune);
    }
  };
  roots.forEach(prune);
  return roots;
}

/** recursively strip
 *    • undefined / null
 *    • false
 *    • empty array / empty object
 */
function cleanObject(o: any): any {
  if (Array.isArray(o)) {
    const arr = o
      .map(cleanObject)
      .filter(v => v !== undefined && v !== null && v !== false &&
        !(Array.isArray(v) && v.length === 0) &&
        !(typeof v === 'object' && Object.keys(v).length === 0));
    return arr.length ? arr : undefined;
  }
  if (typeof o === 'object' && o !== null) {
    const obj: any = {};
    Object.entries(o).forEach(([k, v]) => {
      const cv = cleanObject(v);
      if (
        cv !== undefined && cv !== null && cv !== false &&
        !(Array.isArray(cv) && cv.length === 0) &&
        !(typeof cv === 'object' && Object.keys(cv).length === 0)
      ) {
        obj[k] = cv;
      }
    });
    return Object.keys(obj).length ? obj : undefined;
  }
  return o;
}

function ViewManager() {
  // State for CSV ui_config and the view definition JSON.
  const [uiConfig, setUIConfig] = useState<NavigationRow[]>([]);
  const [viewData, setViewData] = useState<ViewItem[] | null>(null);

  // Navigation state for context, top nav, left nav, and config selection.
  const [selectedTopNav, setSelectedTopNav] = useState("");
  const [selectedLeftNav, setSelectedLeftNav] = useState("");
  const [selectedView, setSelectedView] = useState("");
  const [selectedConfig, setSelectedConfig] = useState("");

  // New state: planning option (as selected via planning options checkboxes).
  const [isTopDown, setIsTopDown] = useState(false);
  const [isBottomUp, setIsBottomUp] = useState(false);

  // New state: derived view name.
  const [derivedViewName, setDerivedViewName] = useState("");

  // State to track unsaved changes.
  const [unsavedData, setUnsavedData] = useState<ViewItem[] | null>(null);
  const [unsavedTdOnlyMap, setUnsavedTdOnlyMap] = useState<Record<string, boolean>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [rootProps, setRootProps] = useState<Record<string, any>>({});

  // State for optimistic locking
  const [lastModifiedAt, setLastModifiedAt] = useState<string | null>(null);
  const [viewType, setViewType] = useState("");
  const [unsavedRootProps, setUnsavedRootProps] = useState<Record<string, any> | null>(null);
  const allowTDOnly = isTopDown;
  const [itemCatalog, setItemCatalog] = useState<Record<string, any>>({});

  // OCI sync state
  const [ociAvailable, setOciAvailable] = useState(false);
  const [ociSyncing, setOciSyncing] = useState(false);

  // Excel download/upload state
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Chatbot state
  const [chatOpen, setChatOpen] = useState(false);

  const apiClient = useApiClient();

  useEffect(() => {
    apiClient.get('/data-items')
      .then(response => {
        const list: any[] = response.data;
        const map: Record<string, any> = {};
        list.forEach(item => { map[item.data_index] = item; });
        setItemCatalog(map);
      })
      .catch(console.error);
  }, [apiClient]);

  const itemCatalogKeys = useMemo(() => Object.keys(itemCatalog), [itemCatalog]);

  // Check OCI availability on mount
  useEffect(() => {
    const checkOCI = async () => {
      try {
        const response = await apiClient.get('/view-definition/oci/check');
        setOciAvailable(response.data.available);
      } catch (error) {
        console.error('Failed to check OCI availability:', error);
        setOciAvailable(false);
      }
    };
    checkOCI();
  }, [apiClient]);

  // ────────────────────────────────────────────────────────
  // 1️⃣ Load navigation rows only once
  useEffect(() => {
    apiClient.get('/navigation')
      .then(response => {
        const data: NavigationRow[] = response.data;
        setUIConfig(data);
        // Don't auto-select first item - wait for user to make a selection
      })
      .catch(console.error);
  }, [apiClient]);  // ← only on mount

  // 2️⃣ Skip clearing on initial seed; clear only on subsequent changes
  const topFirst = useRef(true);
  useEffect(() => {
    if (topFirst.current) {
      topFirst.current = false;
      return;
    }
    setSelectedLeftNav("");
    setSelectedView("");
    setSelectedConfig("");
    setViewData(null);
    setRootProps({});
    setUnsavedData(null);
    setUnsavedRootProps(null);
    setUnsavedTdOnlyMap({});
    setIsDirty(false);
  }, [selectedTopNav]);

  // 3️⃣ Clear downstream only after first mount seed
  const leftFirst = useRef(true);
  useEffect(() => {
    if (leftFirst.current) {
      leftFirst.current = false;
      return;
    }
    setSelectedView("");
    setSelectedConfig("");
    setViewData(null);
    setRootProps({});
    setUnsavedData(null);
    setUnsavedRootProps(null);
    setUnsavedTdOnlyMap({});
    setIsDirty(false);
  }, [selectedLeftNav]);

  // 4️⃣ Clear downstream only after first mount seed
  const viewFirst = useRef(true);
  useEffect(() => {
    if (viewFirst.current) {
      viewFirst.current = false;
      return;
    }
    setSelectedConfig("");
    setViewData(null);
    setRootProps({});
    setUnsavedData(null);
    setUnsavedRootProps(null);
    setUnsavedTdOnlyMap({});
    setIsDirty(false);
  }, [selectedView]);

  // 5️⃣ Load the view from Postgres API whenever all four selectors are set
  useEffect(() => {
    if (
      selectedTopNav &&
      selectedLeftNav &&
      selectedView &&
      selectedConfig
    ) {
      const matchingRow = uiConfig.find(r =>
        r.topNav === selectedTopNav &&
        r.leftNav === selectedLeftNav &&
        r.viewLabel === selectedView &&
        r.configName === selectedConfig
      );
      if (!matchingRow) {
        console.error("No matching navigation row for", {
          selectedTopNav, selectedLeftNav, selectedView, selectedConfig
        });
        setViewData(null);
        return;
      }

      apiClient.get(`/view-definition/${matchingRow.viewKey}`)
        .then(response => {
          const { items, viewType: vt, rootProps: rp, lastModifiedAt: lma } = response.data;
          setViewType(vt);
          setRootProps(rp);
          setDerivedViewName(matchingRow.viewKey);
          setViewData(items);
          setLastModifiedAt(lma);
          setUnsavedData(items);
          setIsDirty(false);
        })
        .catch(err => {
          console.error(err);
          setViewData(null);
        });
    }
  }, [
    selectedTopNav,
    selectedLeftNav,
    selectedView,
    selectedConfig,
    uiConfig
  ]);

  /* build payload for POST */
  const buildSavePayload = () => ({
    rootProps: unsavedRootProps ?? rootProps,
    items: unsavedData ?? viewData,
    lastModifiedAt: lastModifiedAt,
  });

  /* central handler: called by every editor when something changes */
  const markDirty = useCallback((rows: ViewItem[], tdMap: Record<string, boolean>, rp = rootProps) => {
    setUnsavedData(rows);
    setUnsavedRootProps(rp);
    setUnsavedTdOnlyMap(tdMap);
    setIsDirty(true);
  }, [rootProps]);

  // New: handle editing of rootProps in the props‐only editor
  const handleRootPropsChange = useCallback((newRootProps: Record<string, any>) => {
    setRootProps(newRootProps);
    setUnsavedRootProps(newRootProps);
    // re-use the same dirty payload (items stay the same)
    markDirty(unsavedData ?? viewData ?? [], unsavedTdOnlyMap, newRootProps);
  }, [markDirty, unsavedData, viewData, unsavedTdOnlyMap]);

  const handleRowsChange = (rows: ViewItem[]) => {
    console.log('DEBUG handleRowsChange: Received rows with label_text:', rows.map(r => ({ item_id: r.item_id, label_text: r.label_text })));
    // 1) Immediately update the UI
    setViewData(rows);
    // 2) Mark "dirty" so your Save button knows there are changes
    markDirty(rows, unsavedTdOnlyMap);
  };

  /* update callbacks passed to DynamicForm ------------------------- */
  const handleViewDataChange = (rows: ViewItem[]) => markDirty(rows, unsavedTdOnlyMap);
  const handleDirtyChange = (rows: ViewItem[], tdMap: Record<string, boolean>) => {
    markDirty(rows, tdMap);
  };

  // Recursive function to filter data in App.js.
  const filterData = (data: any, tdMap: Record<string, boolean>, currentPath = ""): any => {
    if (typeof data !== "object" || data === null) return data;

    if (Array.isArray(data)) {
      return data
        .map((item, index) => {
          const keyPath = currentPath ? `${currentPath}.${index}` : `${index}`;
          if (tdMap[keyPath] === true) {
            return undefined;
          }
          return filterData(item, tdMap, keyPath);
        })
        .filter((item) => item !== undefined);
    }

    const result: any = {};
    Object.keys(data).forEach((key) => {
      const keyPath = currentPath ? `${currentPath}.${key}` : key;
      if (tdMap[keyPath] === true) return; // skip this key if checked
      result[key] = filterData(data[key], tdMap, keyPath);
    });
    return result;
  };

  const sanitizeData = (data: any): any => {
    if (Array.isArray(data)) {
      return data.map(item => sanitizeData(item)); // Recursively sanitize each item in the array
    } else if (typeof data === 'object' && data !== null) {
      const { _isNew, ...sanitized } = data; // Destructure to remove _isNew
      Object.keys(sanitized).forEach(key => {
        const value = sanitized[key];

        // Handle empty strings based on field type
        if (value === '') {
          // Integer fields - convert empty to proper default
          if (key === 'seq_no') {
            sanitized[key] = 0;
          }
          // Nullable integer/numeric fields
          else if (key === 'width' || key === 'pinned') {
            sanitized[key] = null;
          }
          // String fields with defaults
          else if (key === 'role') {
            sanitized[key] = 'leaf';
          }
          // All other empty strings become null (safer for database)
          else if (key !== 'item_id' && key !== 'view_name') {
            sanitized[key] = null;
          }
        } else {
          // Recursively sanitize nested objects
          sanitized[key] = sanitizeData(value);
        }
      });
      return sanitized;
    }
    return data; // Return primitive values as is
  };

  // Save JSON button handler.
  const saveData = async () => {
    // Produce the filtered JSON by excluding keys where the TD Only checkbox is checked.
    const filteredJson = filterData(unsavedData, unsavedTdOnlyMap);

    // Sanitize both filteredJson and fullJson to remove _isNew
    const sanitizedFilteredJson = sanitizeData(filteredJson);
    const sanitizedFullJson = sanitizeData(unsavedData);

    // Build payload with SANITIZED data
    const payload = {
      rootProps: unsavedRootProps ?? rootProps,
      items: sanitizedFullJson  // Use sanitized data, not raw unsavedData!
    };

    // Debug: Log the payload to see what we're sending
    console.log('Saving payload:', payload);
    console.log('Sanitized items:', sanitizedFullJson);

    // Check for any remaining empty strings in critical fields
    if (sanitizedFullJson && Array.isArray(sanitizedFullJson)) {
      sanitizedFullJson.forEach((item: any, index: number) => {
        if (item.seq_no === '') console.warn(`Row ${index} has empty seq_no`);
        if (item.width === '') console.warn(`Row ${index} has empty width`);
        if (item.pinned === '') console.warn(`Row ${index} has empty pinned`);
      });
    }

    try {
      const r = await apiClient.post(`/view-definition/${derivedViewName}`, payload);

      if (r.status === 409) {
        const conflictData = r.data;
        const shouldRefresh = window.confirm(
          `Conflict detected: ${conflictData.message}\n\n` +
          'This view has been modified by another user. Your changes will be lost if you refresh.\n\n' +
          'Click OK to refresh and lose your changes, or Cancel to keep editing.'
        );

        if (shouldRefresh) {
          window.location.reload();
        }
        return;
      }

      alert('Saved to database 🎉');
      setIsDirty(false);

      // Update our lastModifiedAt timestamp after successful save
      const now = new Date().toISOString();
      setLastModifiedAt(now);
    } catch (err: any) {
      console.error('Save error:', err);
      alert(err.message);
    }
  };

  // Warn user about unsaved changes on unload.
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isDirty]);

  async function handleExport() {
    if (!viewData) return;

    // Helper: recursively strip item_id and parent_item_id from nodes
    function stripIds(node: any) {
      delete node.item_id;
      delete node.parent_item_id;
      if (Array.isArray(node.view)) node.view.forEach(stripIds);
    }

    // Helper: recursively filter out TD-only rows from the tree
    const filterTreeForTDOnly = (nodes: any[], tdOnlyMap: Record<string, boolean>, currentPath = '') => {
      return nodes
        .filter(node => {
          const pathKey = currentPath ? `${currentPath}.${node.item_id}` : node.item_id;
          // Exclude node if marked as TD-only
          return !tdOnlyMap[pathKey];
        })
        .map(node => ({
          ...node,
          view: node.view ? filterTreeForTDOnly(node.view, tdOnlyMap, `${currentPath}.${node.item_id}`) : undefined
        }));
    };

    // Build the full data (all records)
    const treeFull = buildView(viewData);
    treeFull.forEach(stripIds);
    const fullData = cleanObject({ type: viewType, ...rootProps, view: treeFull });

    // Build the filtered data (exclude TD-only checked rows)
    const hasFiltered = Object.values(unsavedTdOnlyMap).some(Boolean);
    let filteredData;

    if (hasFiltered) {
      const filteredTree = filterTreeForTDOnly(treeFull, unsavedTdOnlyMap);
      filteredTree.forEach(stripIds);
      filteredData = cleanObject({ type: viewType, ...rootProps, view: filteredTree });
    } else {
      // If no TD-only checks, filtered = full
      filteredData = fullData;
    }

    // File names setup
    const nameFull = `TD_${selectedConfig}.viewdefn`;
    const nameFiltered = `${selectedConfig}.viewdefn`;

    // Create blobs
    const blobFull = new Blob([JSON.stringify(fullData, null, 2)], { type: "application/json" });
    const blobFiltered = new Blob([JSON.stringify(filteredData, null, 2)], { type: "application/json" });

    // Download full TD file always only if hasFiltered true
    if (hasFiltered) {
      const url = URL.createObjectURL(blobFull);
      const a = document.createElement("a");
      a.href = url;
      a.download = nameFull;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    }

    // Download filtered file always
    {
      const url = URL.createObjectURL(blobFiltered);
      const a = document.createElement("a");
      a.href = url;
      a.download = nameFiltered;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    }
  }

  // Handler for syncing to OCI
  const handleSyncToOCI = useCallback(async () => {
    if (!viewData || !selectedConfig) return;

    if (!window.confirm('This will save all pending changes and sync the view definition to OCI. Continue?')) {
      return;
    }

    try {
      setOciSyncing(true);

      // Save pending changes first if any
      if (isDirty) {
        await saveData();
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Build the preview data (same as what's shown in preview)
      const tree = buildView(viewData);

      function stripIds(node: any) {
        delete node.item_id;
        delete node.parent_item_id;
        if (Array.isArray(node.view)) node.view.forEach(stripIds);
      }
      tree.forEach(stripIds);

      const cleanedView = cleanObject(tree);
      const viewJson = {
        type: viewType,
        ...rootProps,
        view: cleanedView,
      };

      // Sync to OCI - just send the JSON and viewKey
      const response = await apiClient.post(`/view-definition/${encodeURIComponent(selectedConfig)}/sync-to-oci`, viewJson);

      if (response.data.success) {
        alert('View definition synced to OCI successfully!');
      } else {
        throw new Error(response.data.error || 'Sync failed');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.details || err.response?.data?.error || err.message || 'Failed to sync to OCI';
      alert(`Sync failed: ${errorMessage}`);
      console.error('OCI sync error:', err);
    } finally {
      setOciSyncing(false);
    }
  }, [viewData, selectedConfig, isDirty, viewType, rootProps, apiClient, saveData]);

  // Excel download handler
  const handleDownloadExcel = useCallback(async () => {
    if (!selectedConfig) {
      alert('Please select a view first');
      return;
    }

    try {
      const response = await apiClient.get(`/view-definition/${encodeURIComponent(selectedConfig)}/export-excel`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedConfig}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to download Excel';
      alert(`Download failed: ${errorMessage}`);
      console.error('Excel download error:', err);
    }
  }, [apiClient, selectedConfig]);

  // Excel upload handler
  const handleUploadExcel = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedConfig) return;

    if (!window.confirm('This will replace the current view definition with data from the Excel file. Continue?')) {
      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    try {
      setUploadingExcel(true);

      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post(`/view-definition/${encodeURIComponent(selectedConfig)}/import-excel`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        alert(`Successfully imported ${response.data.itemsImported} items!`);
        // Reload the view data by triggering the useEffect
        const currentConfig = selectedConfig;
        setSelectedConfig('');
        setTimeout(() => setSelectedConfig(currentConfig), 100);
      } else {
        throw new Error(response.data.error || 'Import failed');
      }
    } catch (err: any) {
      const errorDetails = err.response?.data?.details;
      const errorMessage = err.response?.data?.error || err.message || 'Failed to upload Excel';

      if (Array.isArray(errorDetails)) {
        alert(`Import failed:\n\n${errorDetails.join('\n')}`);
      } else {
        alert(`Import failed: ${errorMessage}`);
      }
      console.error('Excel upload error:', err);
    } finally {
      setUploadingExcel(false);
      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [apiClient, selectedConfig]);

  /* ---------------- preview data prep ---------------- */
  const currentRows = unsavedData ?? viewData;
  let previewData = null;
  if (currentRows) {
    // 2‑A  Build the nested tree
    const tree = buildView(currentRows);

    // 2‑B  Strip IDs after the tree is wired
    function stripIds(node: any) {
      delete node.item_id;
      delete node.parent_item_id;
      if (Array.isArray(node.view)) node.view.forEach(stripIds);
    }
    tree.forEach(stripIds);

    const cleanedView = cleanObject(tree);

    previewData = {
      type: viewType,
      ...rootProps,
      view: cleanedView,
    };
  }
  /* --------------------------------------------------- */

  console.log('📚 SystemConfiguration itemCatalog size:', Object.keys(itemCatalog || {}).length);
  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <Container maxWidth="lg" sx={{ marginTop: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h4">
              View Configuration
            </Typography>
            <Button
              variant="outlined"
              startIcon={<ChatIcon />}
              onClick={() => setChatOpen(!chatOpen)}
              color="primary"
            >
              {chatOpen ? 'Close AI' : 'Ask AI'}
            </Button>
          </Box>
          <Paper sx={{ padding: 2, marginBottom: 2 }}>
            <NavigationPanel
              uiConfig={uiConfig}
              selectedTopNav={selectedTopNav}
              setSelectedTopNav={setSelectedTopNav}
              selectedLeftNav={selectedLeftNav}
              setSelectedLeftNav={setSelectedLeftNav}
              selectedView={selectedView}
              setSelectedView={setSelectedView}
              selectedConfig={selectedConfig}
              setSelectedConfig={setSelectedConfig}
              onSetIsTopDown={setIsTopDown}
              onSetIsBottomUp={setIsBottomUp}
            />
          </Paper>
          <Paper sx={{ padding: 2, marginBottom: 2 }}>
            {Object.keys(rootProps).length === 0 ? (
              <Typography variant="body1">
                Please select a configuration to load its view definition.
              </Typography>
            ) : viewData && viewData.length > 0 ? (
              // 1️⃣ We have items — show the full table + props editor
              <DynamicForm
                viewRows={viewData}
                allowTDOnly={allowTDOnly}
                rootProps={rootProps}
                onDataChange={handleRowsChange}
                onRootChange={handleRootPropsChange}
                viewType={viewType}
                isTopDown={isTopDown}
                onDirtyChange={handleDirtyChange}
                itemCatalog={itemCatalog}
                itemCatalogKeys={itemCatalogKeys}
                readOnly={true}
              />
            ) : (
              // 2️⃣ No items — just show the props JSON editor
              <Box sx={{ p: 2 }}>
                <DynamicForm
                  viewRows={[]}
                  rootProps={rootProps}
                  onDataChange={handleViewDataChange}
                  onRootChange={handleRootPropsChange}
                  itemCatalog={itemCatalog}
                  itemCatalogKeys={itemCatalogKeys}
                  allowTDOnly={false}
                  readOnly={true}
                />
              </Box>
            )}
          </Paper>


          <Paper sx={{ p: 2, mb: 2 }}>
            <PreviewPanel data={previewData} />
          </Paper>

          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              type="button"
              onClick={(e) => {
                e.preventDefault();
                saveData();
              }}
            >
              Save Data (⇧⌘S)
            </Button>
            <Button
              variant="outlined"
              onClick={handleExport}
              disabled={!previewData}>
              Export View(⇧⌘E)
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              onClick={handleDownloadExcel}
              disabled={!selectedConfig}
            >
              Download Excel
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              component="label"
              disabled={!selectedConfig || uploadingExcel}
            >
              {uploadingExcel ? 'Uploading...' : 'Upload Excel'}
              <input
                ref={fileInputRef}
                type="file"
                hidden
                accept=".xlsx,.xls"
                onChange={handleUploadExcel}
              />
            </Button>

            {ociAvailable && (
              <Button
                variant="contained"
                size="small"
                color="primary"
                onClick={handleSyncToOCI}
                disabled={!viewData || ociSyncing}
              >
                {ociSyncing ? 'Syncing...' : 'Sync to OCI'}
              </Button>
            )}

            {isDirty && (
              <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                You have unsaved changes!
              </Typography>
            )}
          </Box>
        </Container>
      </Box>

      {/* Chatbot Drawer */}
      <Drawer
        anchor="right"
        open={chatOpen}
        variant="persistent"
        sx={{
          width: 450,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 450,
            boxSizing: 'border-box'
          }
        }}
      >
        <ChatbotPanel
          currentView={selectedView || selectedConfig}
          currentViewId={derivedViewName}
          onClose={() => setChatOpen(false)}
        />
      </Drawer>
    </Box>
  );

}
export default ViewManager;